import { useEffect, useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable";
import {
  Drawer,
  DrawerContent,
} from "@/components/ui/drawer";
import { FilterIcon } from "lucide-react";
import { useAuth } from "@clerk/react";
import { CreatorAction } from "@/types/types";
import ActionDetailsPanel from "./ActionDetailsPanel";
import ActionsListPanel from "./ActionsListPanel";
import { useDashboardStore } from "@/stores";
import { fetchThreadMessages } from "@/stores/inbox/inboxStore";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useIsMobile } from "@/hooks/use-mobile";
import { useComplianceRole } from "@/hooks/IsAdminContext";

/** Amy-v2 needs-attention rows grouped under one "Amy" filter. */
const AMY_ACTION_TYPES = ["phase-review", "payout-confirm", "eve-attention", "contract-change", "offer-review"];
/** Rows whose detail view shows the creator's email thread. */
const THREAD_ACTION_TYPES = ["handoff", "next-message", "contract-change", "offer-review"];

/** Filterable task categories (multi-select; none checked = all). */
type FilterType = "handoff" | "waiting-approval" | "next-message" | "performance-payment" | "amy";
const FILTER_CONFIG: { value: FilterType; label: string }[] = [
  { value: "handoff", label: "Handoff" },
  { value: "waiting-approval", label: "Approval" },
  { value: "next-message", label: "Messaging" },
  { value: "performance-payment", label: "Payments" },
  { value: "amy", label: "Amy" },
];
const FILTER_VALUES = FILTER_CONFIG.map((f) => f.value);

const matchesFilter = (action: CreatorAction, filter: FilterType) =>
  filter === "amy"
    ? AMY_ACTION_TYPES.includes(action.actionType)
    : action.actionType === filter;

export default function TasksDashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [actionLoading, setActionLoading] = useState<boolean>(false);
  const [emailThread, setEmailThread] = useState<any[]>([]);
  const [threadLoading, setThreadLoading] = useState<boolean>(false);
  const isMobile = useIsMobile();

  // Selected filters persist in ?tab= as a comma list (unknown values,
  // incl. the pre-dropdown "all", are dropped → no filter).
  const tabParam = searchParams.get("tab");
  const selectedFilters = useMemo(
    () =>
      (tabParam ?? "")
        .split(",")
        .filter((v): v is FilterType => (FILTER_VALUES as string[]).includes(v)),
    [tabParam]
  );
  const actionId = searchParams.get("action");

  const { orgId, getToken } = useAuth();
  const { tasks, tasksLoading, fetchTasks } = useDashboardStore();
  // Compliance reviewers see only phase-review rows and no tab bar. The
  // backend already filters getUserTasks for them; this is the UI mirror.
  const { isCompliance } = useComplianceRole();

  const visibleTasks = useMemo(
    () =>
      isCompliance
        ? tasks.filter((t) => t.actionType === "phase-review")
        : tasks,
    [tasks, isCompliance]
  );

  const selectedAction = useMemo(() =>
    actionId ? visibleTasks.find((t) => t._id === actionId) ?? null : null,
    [actionId, visibleTasks]
  );

  const filteredActions = useMemo(() => {
    if (isCompliance || selectedFilters.length === 0) return visibleTasks;
    return visibleTasks.filter((a) => selectedFilters.some((f) => matchesFilter(a, f)));
  }, [selectedFilters, visibleTasks, isCompliance]);

  const toggleFilter = (checked: boolean, value: FilterType) => {
    const next = checked
      ? [...selectedFilters, value]
      : selectedFilters.filter((v) => v !== value);
    setSearchParams(next.length ? { tab: next.join(",") } : {});
  };

  const handleActionClick = (action: CreatorAction) => {
    const params: Record<string, string> = {};
    if (selectedFilters.length) params.tab = selectedFilters.join(",");
    params.action = action._id;
    setSearchParams(params);

    setEmailThread([]);

    if (THREAD_ACTION_TYPES.includes(action.actionType)) {
      fetchInbox(action._id, action.is_eve);
    }
  };

  const handleDrawerClose = (open: boolean) => {
    if (!open) {
      // Remove action from URL when drawer closes
      const params: Record<string, string> = {};
      if (selectedFilters.length) params.tab = selectedFilters.join(",");
      setSearchParams(params, { replace: true });
      setEmailThread([]);
    }
  };

  const fetchInbox = async (campaignCreatorId: string, isEve?: boolean) => {
    try {
      setThreadLoading(true);
      const token = await getToken();
      if (!token) throw new Error("No token");
      // Amy-run rows read the thread from Amy's pinned mailbox (Flask reads
      // the campaign owner's Gmail, which does not hold Amy's threads).
      const messages = await fetchThreadMessages(campaignCreatorId, token, { isEve });
      const sortedEmails = [...messages].sort(
        (a, b) =>
          new Date(a.sent_at_iso ?? a.sent_at ?? 0).getTime() -
          new Date(b.sent_at_iso ?? b.sent_at ?? 0).getTime()
      );
      setEmailThread(sortedEmails);
    } catch (error) {
      console.error("Error fetching thread:", error);
    } finally {
      setThreadLoading(false);
    }
  };

  useEffect(() => {
    if (actionLoading || !orgId) return;
    const fetchData = async () => {
      const token = await getToken();
      if (orgId && token) {
        fetchTasks(orgId, token);
      }
    };
    fetchData();
    setActionLoading(true);
  }, [actionLoading, orgId, getToken, fetchTasks]);

  useEffect(() => {
    if (actionId && !tasksLoading && visibleTasks.length > 0) {
      const actionStillExists = visibleTasks.some((task) => task._id === actionId);
      if (!actionStillExists) {
        const params: Record<string, string> = {};
        if (selectedFilters.length) params.tab = selectedFilters.join(",");
        setSearchParams(params, { replace: true });
        setEmailThread([]);
      }
    }
  }, [visibleTasks, actionId, tasksLoading, selectedFilters, setSearchParams]);

  useEffect(() => {
    if (selectedAction && emailThread.length === 0 && !threadLoading) {
      if (THREAD_ACTION_TYPES.includes(selectedAction.actionType)) {
        fetchInbox(selectedAction._id, selectedAction.is_eve);
      }
    }
  }, [selectedAction?._id]);

  const detailsPanel = (
    <ActionDetailsPanel
      selectedAction={selectedAction}
      emailThread={emailThread}
      threadLoading={threadLoading}
      setActionLoading={setActionLoading}
    />
  );

  const listContent = (
    <>
      <div className="flex items-center justify-between gap-2 pl-5 p-2.5 sticky top-0 z-10 bg-background border-b">
        <h1 className="text-2xl font-semibold">Tasks</h1>
        {!isCompliance && (
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="secondary" size="sm">
                <FilterIcon
                  className="-ms-1 opacity-60"
                />
                Filter
                {selectedFilters.length > 0 && (
                  <Badge variant="count" className="-me-1">
                    {selectedFilters.length}
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="w-auto min-w-36 p-1"
              align="end"
            >
              <div className="space-y-2">
                <div className="text-muted-foreground text-xs font-medium p-2 pb-0">
                  Filters
                </div>
                <div className="">
                  {FILTER_CONFIG.map(({ value, label }, i) => {
                    const count =
                      value === "amy"
                        ? visibleTasks.filter((a) => AMY_ACTION_TYPES.includes(a.actionType)).length
                        : visibleTasks.filter((a) => a.actionType === value).length;

                    return (
                      <div
                        key={value}
                        className="flex items-center gap-2 p-2 pr-3 hover:bg-muted rounded-sm cursor-pointer"
                        onClick={() =>
                          toggleFilter(!selectedFilters.includes(value), value)
                        }
                      >
                        <Checkbox
                          id={`tasks-filter-${i}`}
                          checked={selectedFilters.includes(value)}
                          onCheckedChange={(checked: boolean) =>
                            toggleFilter(checked, value)
                          }
                        />
                        <Label className="flex grow justify-between gap-2 font-normal cursor-pointer text-xs">
                          {label}{" "}
                          <span className="text-muted-foreground ms-2">
                            {count}
                          </span>
                        </Label>
                      </div>
                    );
                  })}
                </div>
              </div>
            </PopoverContent>
          </Popover>
        )}
      </div>
      <ActionsListPanel
        filteredActions={filteredActions}
        selectedAction={selectedAction}
        onActionClick={handleActionClick}
        loading={tasksLoading}
      />
    </>
  );

  if (isMobile) {
    return (
      <div className="flex-1 flex flex-col h-full overflow-hidden">
        <ScrollArea className="h-full">{listContent}</ScrollArea>
        <Drawer
          open={!!selectedAction}
          onOpenChange={handleDrawerClose}
        >
          <DrawerContent className="h-[85vh] max-h-[85vh] p-0">
            <div className="h-full overflow-auto">{detailsPanel}</div>
          </DrawerContent>
        </Drawer>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden">
      <ResizablePanelGroup
        direction="horizontal"
        className="flex flex-row h-full bg-background"
      >
        <ResizablePanel defaultSize={40} minSize={40} maxSize={55}>
          <ScrollArea className="h-full">{listContent}</ScrollArea>
        </ResizablePanel>
        <ResizableHandle withHandle className="hover:ring-2 active:ring-2 ring-primary/20 hover:bg-primary active:bg-primary transition-all" />
        <ResizablePanel>{detailsPanel}</ResizablePanel>
      </ResizablePanelGroup>
    </div>
  );
}